import { EditorView } from 'prosemirror-view'
import { QueryClient, QueryClientProvider } from 'react-query'
import { FloatingAIRevisionContainer } from 'components/AIRevision/AIRevisionContainer'
import { AIRevisionPluginKey } from 'common/Prose/Plugins/AIRevision/constants'
import { AlmanacEditorState, AlmanacEditorStateContext } from 'stores/EditorState'
import { State } from 'common/Prose/Plugins/AIRevision/Types'
import { createRoot, Root } from 'react-dom/client'
import { ModalStack } from 'almanac-ui/Modal'
import { IStackManager } from 'almanac-ui/Modal/Types'
import { Provider } from 'react-redux'
import store from 'redux/store'
import { Router } from 'react-router-dom'

export class AIRevisionView {
  container: HTMLDivElement
  view: EditorView
  queryClient: QueryClient
  layerId: string
  articleId: string
  revisionUuid: string | null
  almanacEditorState: AlmanacEditorState
  root: Root
  stackManager: IStackManager

  constructor(view: EditorView) {
    this.view = view
    this.container = document.createElement('div')
    const { queryClient, articleId, layerId, stackManager } = AIRevisionPluginKey.getState(this.view.state) as State
    this.queryClient = queryClient
    this.articleId = articleId
    this.layerId = layerId
    this.revisionUuid = null
    this.almanacEditorState = new AlmanacEditorState(view)
    this.root = createRoot(this.container)
    this.stackManager = stackManager
  }

  update(view: EditorView) {
    this.view = view
    let revisionUuid: string | undefined
    this.almanacEditorState.updateFromView(view)

    if (!this.view.dom.parentNode) return

    view.state.doc.descendants((node) => {
      const relevantMarks = node.marks.filter((mark) => mark.type.name === 'ai_revision')
      if (relevantMarks.length > 0) {
        revisionUuid = relevantMarks[0].attrs.uuid
      }

      return !revisionUuid
    })

    if (!revisionUuid && !!this.revisionUuid) {
      this.view.dom.parentNode.removeChild(this.container)
      this.revisionUuid = null
    }

    if (!revisionUuid || revisionUuid === this.revisionUuid) return

    const markNodes = this.view.dom.querySelectorAll(`[data-revision-uuid='${revisionUuid}']`)
    const anchor = markNodes.item(markNodes.length - 1)

    if (!anchor) return

    this.view.dom.parentNode.appendChild(this.container)

    this.root.render(
      <Provider store={store}>
        <Router history={(window as any).hist}>
          <QueryClientProvider client={this.queryClient}>
            <AlmanacEditorStateContext.Provider value={this.almanacEditorState}>
              <ModalStack stackManager={this.stackManager}>
                <FloatingAIRevisionContainer
                  anchor={anchor as HTMLDivElement}
                  id={revisionUuid}
                  articleId={this.articleId}
                  layerId={this.layerId}
                />
              </ModalStack>
            </AlmanacEditorStateContext.Provider>
          </QueryClientProvider>
        </Router>
      </Provider>,
    )

    anchor.scrollIntoViewIfNeeded()

    this.revisionUuid = revisionUuid
  }

  destroy() {
    setTimeout(() => this.root.unmount(), 0)
    this.container.remove()
  }
}
